import { forwardRef, useMemo, type AnchorHTMLAttributes } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router";

/**
 * Next.js의 next/link · next/navigation 자리를 대신하는 얇은 어댑터. 컴포넌트들이
 * 쓰던 모양(<Link href>, useRouter().push, usePathname)을 그대로 두고 react-router 위에서
 * 돌게 해서, 마이그레이션 때 컴포넌트 본문을 거의 안 건드리게 한다.
 */
type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & { href: string };

const isExternal = (href: string) => /^(https?:)?\/\//.test(href) || href.startsWith("mailto:");

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link({ href, ...props }, ref) {
  // target=_blank나 외부 주소는 라우터를 거치지 않고 일반 앵커로 둔다.
  if (isExternal(href) || props.target === "_blank") {
    return <a ref={ref} href={href} rel={props.target === "_blank" ? "noreferrer" : undefined} {...props} />;
  }
  return <RouterLink ref={ref} to={href} {...props} />;
});

/** router.refresh()가 쏘는 이벤트 — 데이터를 다시 불러와야 하는 화면(useLoader)이 듣는다. */
export const REFRESH_EVENT = "app:refresh";

export function useRouter() {
  const navigate = useNavigate();
  return useMemo(
    () => ({
      push: (href: string) => navigate(href),
      replace: (href: string) => navigate(href, { replace: true }),
      back: () => navigate(-1),
      // Next의 router.refresh()(서버 컴포넌트 재실행)에 해당 — 화면 데이터를 다시 불러온다.
      refresh: () => window.dispatchEvent(new Event(REFRESH_EVENT)),
    }),
    [navigate],
  );
}

export function usePathname() {
  return useLocation().pathname;
}
